import { useState, useRef, useEffect } from 'react';
import { StoreColor } from '../../models';

interface ColorPickerFilterProps {
  colors: StoreColor[];
  value?: number;
  onChange: (id: number | undefined) => void;
}

export function ColorPickerFilter({ colors, value, onChange }: ColorPickerFilterProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const selected = colors.find((c) => c.id === value);

  const filtered = query.trim()
    ? colors.filter((c) => c.name.toLowerCase().includes(query.toLowerCase()))
    : colors;

  useEffect(() => {
    if (!open) return;
    searchRef.current?.focus();
  }, [open]);

  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  const handleSelect = (id: number | undefined) => {
    onChange(id);
    if (id !== undefined) setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={[
          'flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors',
          open || value !== undefined
            ? 'border-chocolate text-text-dark'
            : 'border-champagne-dark text-text-medium hover:border-chocolate/60',
        ].join(' ')}
      >
        {selected ? (
          <>
            <span
              className="w-4 h-4 rounded-full border border-champagne-dark shrink-0"
              style={{ backgroundColor: selected.hexadecimal }}
            />
            <span>{selected.name}</span>
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); onChange(undefined); }}
              onKeyDown={(e) => e.key === 'Enter' && onChange(undefined)}
              className="text-text-light hover:text-chocolate cursor-pointer"
              aria-label="Remover cor"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </span>
          </>
        ) : (
          <>
            <svg className="w-4 h-4 text-text-light" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="9" strokeWidth={1.5} />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M12 3C9 6 7 9 7 12s2 6 5 9c3-3 5-6 5-9s-2-6-5-9z" />
            </svg>
            <span>Cor</span>
          </>
        )}
        <svg
          className={`w-4 h-4 text-text-light transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute top-full left-0 z-50 mt-1 bg-white border border-champagne-dark rounded-xl shadow-lg w-72 max-w-[calc(100vw-2rem)] overflow-hidden">
          {/* Search */}
          <div className="p-2 border-b border-champagne">
            <div className="relative">
              <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-light" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
              </svg>
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar por nome..."
                className="w-full pl-8 pr-3 py-1.5 text-sm rounded-lg border border-champagne-dark focus:outline-none focus:border-chocolate bg-champagne-light"
              />
            </div>
          </div>

          {/* All option */}
          <div className="px-3 pt-2 pb-1">
            <button
              type="button"
              onClick={() => { handleSelect(undefined); setOpen(false); }}
              className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                value === undefined
                  ? 'border-chocolate bg-champagne text-chocolate font-medium'
                  : 'border-champagne-dark text-text-light hover:border-chocolate/50 hover:text-text-medium'
              }`}
            >
              Todas as cores
            </button>
          </div>

          {/* Color swatches */}
          <div className="px-3 pb-3 max-h-52 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="text-xs text-text-light text-center py-3">Nenhuma cor encontrada</p>
            ) : (
              <div className="flex flex-wrap gap-2 pt-1">
                {filtered.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => handleSelect(c.id)}
                    title={c.name}
                    className={`flex flex-col items-center gap-1 group`}
                  >
                    <span
                      className={`w-8 h-8 rounded-full border-2 block transition-all ${
                        value === c.id
                          ? 'border-chocolate scale-110 shadow-md'
                          : 'border-champagne-dark group-hover:border-chocolate/60 group-hover:scale-105'
                      }`}
                      style={{ backgroundColor: c.hexadecimal }}
                    />
                    <span className={`text-[10px] leading-tight max-w-[3rem] text-center truncate transition-colors ${
                      value === c.id ? 'text-chocolate font-medium' : 'text-text-light'
                    }`}>
                      {c.name}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
